import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardSubtitle,
} from "reactstrap";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { storage, db } from "../common/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import SweetAlert from "react-bootstrap-sweetalert";

const FormUpload = ({ orderData, onFileChange, handleUpload, orderCode, fileLimit, onUploadStatusChange }) => {
  const [selectedFiles, setselectedFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [uploadedUrls, setUploadedUrls] = useState({});
  const [alert, setAlert] = useState(null);

  // ✅ Notify parent when uploads are in progress or done
  useEffect(() => {
    if (Object.keys(progress).length > 0) {
      onUploadStatusChange(true)
      const allDone = Object.values(progress).every(p => p === 100);
      if (onUploadStatusChange && Object.keys(progress).length === Object.keys(uploadedUrls).length) {
        onUploadStatusChange(false); // true = still uploading, false = done
      }
    }
  }, [progress, onUploadStatusChange]);

  function handleAcceptedFiles(files) {
    const processedFiles = files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setselectedFiles(processedFiles);
    onFileChange(files);

    // Start upload immediately
    files.forEach(file => uploadFile(file));
  }

  function sanitizeFileName(filename) {
    const parts = filename.split(".");
    const ext = parts.pop();
    const name = parts.join(".");
    const cleanedName = name
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");
    return `${cleanedName}.${ext.toLowerCase()}`;
  }

  function uploadFile(file) {
    const storageRef = ref(storage, `orderUploads/${orderCode}/${orderCode}-${sanitizeFileName(file.name)}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      snapshot => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(prev => ({ ...prev, [file.name]: prog }));
      },
      error => {
        console.error("Upload error:", error);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setUploadedUrls(prev => ({ ...prev, [file.name]: downloadURL }));

        if (typeof handleUpload === "function") {
          handleUpload(downloadURL, file.name);
        }

        await addDoc(collection(db, "uploads"), {
          name: file.name,
          url: downloadURL,
          size: file.size,
          createdAt: new Date(),
        });

        // Mark file as 100% uploaded
        setProgress(prev => ({ ...prev, [file.name]: 100 }));
      }
    );
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  useEffect(() => {
    if (!orderData) return;

    let updates = {};
    let progressUpdates = {};
    let files = [];

    if (orderData?.fileName && orderData?.uploadfileName) {
      updates[orderData.fileName] = orderData.uploadfileName;
      progressUpdates[orderData.fileName] = 100;
      // handleUpload
      handleUpload(orderData.uploadfileName, orderData.fileName)
      files.push({
        name: orderData.fileName,
        preview: orderData.uploadfileName, // or thumbnail URL
        formattedSize: "Already Uploaded",
      });
    }

    if (orderData?.fileName2 && orderData?.uploadfileName2) {
      updates[orderData.fileName2] = orderData.uploadfileName2;
      progressUpdates[orderData.fileName2] = 100;
      handleUpload(orderData.uploadfileName2, orderData.fileName2)
      files.push({
        name: orderData.fileName2,
        preview: orderData.uploadfileName2, // or thumbnail URL
        formattedSize: "Already Uploaded",
      });
    }

    if (Object.keys(updates).length > 0) {
      setUploadedUrls(prev => ({ ...prev, ...updates }));
      setProgress(prev => ({ ...prev, ...progressUpdates }));
      setselectedFiles(prev => [...prev, ...files]); // 👈 hydrate selectedFiles
    }
  
  }, [orderData]);
  

  
  useEffect(() => {
  }, [progress, uploadedUrls])

  
  return (
    <React.Fragment>
      <h6 className="card-title">Upload Files</h6>
      {alert}
      <CardSubtitle className="mb-3">
        Drag and drop files with image previews & Firebase upload.
      </CardSubtitle>
      <Dropzone
        accept=".jpg, .jpeg, .png, .gif, .bmp, .tiff, .webp, .pdf, .ai, .eps, .psd, .dst, .pes, .exp, .emb, .cnd, .dsz, .jef, .ofm, .vp3, .hus, .svg"
        maxSize={5 * 1024 * 1024}  // 5 MB
        onDrop={handleAcceptedFiles}
        maxFiles={fileLimit}
        onDropRejected={(fileRejections) => {
          setAlert(
            <SweetAlert
              danger
              cancelBtnText="Close"
              title={`You can only upload up to ${fileLimit} files.`}
              onCancel={() => setAlert(null)}
              onConfirm={() => setAlert(null)}
              focusCancelBtn
            >
              <p>{fileRejections.length - fileLimit} extra file(s) rejected.</p>
            </SweetAlert>
          );
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <div className="dropzone" style={{ minHeight: "120px" }}>
            <div
              className="dz-message needsclick mt-2"
              {...getRootProps()}
              style={{ padding: "10px" }}
            >
              <input {...getInputProps()} />
              <div className="mb-3">
                <i className="display-4 text-muted bx bxs-cloud-upload" />
              </div>
              <h4>Drop files here or click to upload.</h4>
              <h6> Allowed Extensions:
                &quot;.jpg&quot;, &quot;.jpeg&quot;, &quot;.png&quot;, &quot;.gif&quot;, &quot;.bmp&quot;,
                &quot;.tiff&quot;, &quot;.webp&quot;, &quot;.pdf&quot;, &quot;.ai&quot;, &quot;.eps&quot;, &quot;.psd&quot;
                
                &quot;,.dst&quot;, .pes&quot;, .exp&quot;, .emb&quot;, .cnd&quot;, .dsz&quot;, .jef&quot;, .ofm&quot;, .vp3&quot;, .hus&quot;, .svg&quot;
                </h6>
            </div>
          </div>
        )}
      </Dropzone>

      {/* File Previews with Progress */}
      <div className="dropzone-previews mt-3" id="file-previews">
        {selectedFiles.map((f, i) => (
          <Card
            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
            key={i + "-file"}
          >
            <div className="p-2">
              <Row className="align-items-center">
                <Col className="col-auto">
                  <img
                    height="80"
                    className="avatar-sm rounded bg-light"
                    alt={f.name}
                    src={f.preview}
                  />
                </Col>
                <Col>
                  <Link to="#" className="text-muted font-weight-bold">
                    {f.name}
                  </Link>
                  <p className="mb-0">
                    <strong>{f.formattedSize}</strong>
                  </p>
                  {progress[f.name] && (
                    <div style={{ marginTop: "5px" }}>
                      <progress value={progress[f.name]} max="100" />
                      <span> {progress[f.name]}%</span>
                    </div>
                  )}
                  {uploadedUrls[f.name] && (
                    <p className="text-success mt-2">
                      ✅ Uploaded:{" "}
                      <a href={uploadedUrls[f.name]} target="_blank" rel="noreferrer">
                        View File
                      </a>
                    </p>
                  )}
                </Col>
              </Row>
            </div>
          </Card>
        ))}
      </div>
    </React.Fragment>
  );
};

FormUpload.propTypes = {
  onFileChange: PropTypes.func.isRequired,
  handleUpload: PropTypes.func,
  orderCode: PropTypes.number,
  fileLimit: PropTypes.number,
  onUploadStatusChange: PropTypes.func,
  orderData: PropTypes.object
};

export default FormUpload;


// import React, { useState, useEffect } from "react";
// import { Row, Col, Card, CardSubtitle } from "reactstrap";
// import Dropzone from "react-dropzone";
// import { Link } from "react-router-dom";
// import PropTypes from "prop-types";
// import SweetAlert from "react-bootstrap-sweetalert";
// import axiosApi from "helpers/api_helper";
// // ✅ Import your shared axios instance — auth token + refresh interceptor already included

// const FormUpload = ({
//   orderData,
//   onFileChange,
//   handleUpload,
//   orderCode,
//   fileLimit,
//   onUploadStatusChange,
// }) => {
//   const [selectedFiles, setSelectedFiles] = useState([]);
//   const [progress, setProgress] = useState({});
//   const [uploadedUrls, setUploadedUrls] = useState({});
//   const [alert, setAlert] = useState(null);

//   // ─── Notify parent when uploads are in progress or done ───────────────────
//   useEffect(() => {
//     if (Object.keys(progress).length === 0) return;

//     onUploadStatusChange?.(true); // signal: uploading started

//     const allDone =
//       Object.keys(progress).length === Object.keys(uploadedUrls).length &&
//       Object.values(progress).every((p) => p === 100);

//     if (allDone) {
//       onUploadStatusChange?.(false); // signal: all done
//     }
//   }, [progress, uploadedUrls, onUploadStatusChange]);

//   // ─── Hydrate from existing orderData (edit mode) ─────────────────────────
//   useEffect(() => {
//     if (!orderData) return;

//     const urlUpdates = {};
//     const progressUpdates = {};
//     const files = [];

//     const hydrate = (fileName, fileUrl) => {
//       if (fileName && fileUrl) {
//         urlUpdates[fileName] = fileUrl;
//         progressUpdates[fileName] = 100;
//         handleUpload?.(fileUrl, fileName);
//         files.push({ name: fileName, preview: fileUrl, formattedSize: "Already Uploaded" });
//       }
//     };

//     hydrate(orderData.fileName, orderData.uploadfileName);
//     hydrate(orderData.fileName2, orderData.uploadfileName2);

//     if (files.length > 0) {
//       setUploadedUrls((prev) => ({ ...prev, ...urlUpdates }));
//       setProgress((prev) => ({ ...prev, ...progressUpdates }));
//       setSelectedFiles((prev) => [...prev, ...files]);
//     }
//   }, [orderData]);

//   // ─── Drop handler ─────────────────────────────────────────────────────────
//   function handleAcceptedFiles(files) {
//     const processed = files.map((file) =>
//       Object.assign(file, {
//         preview: URL.createObjectURL(file),
//         formattedSize: formatBytes(file.size),
//       })
//     );
//     setSelectedFiles(processed);
//     onFileChange(files);
//     files.forEach((file) => uploadFile(file));
//   }

//   // ─── Upload to .NET Core server ───────────────────────────────────────────
//   async function uploadFile(file) {
//     const formData = new FormData();
//     formData.append("file", file);

//     try {
//       // ✅ Use axiosApi directly (NOT the post() helper) so FormData is sent raw
//       // — auth token & refresh interceptor are still applied automatically
//       const response = await axiosApi.post(
//         `upload?orderCode=${orderCode}`,
//         formData,
//         {
//           headers: { "Content-Type": "multipart/form-data" },
//           onUploadProgress: (progressEvent) => {
//             const percent = Math.round(
//               (progressEvent.loaded * 100) / progressEvent.total
//             );
//             setProgress((prev) => ({ ...prev, [file.name]: percent }));
//           },
//         }
//       );

//       const { fileUrl, fileName } = response.data;

//       setUploadedUrls((prev) => ({ ...prev, [file.name]: fileUrl }));
//       setProgress((prev) => ({ ...prev, [file.name]: 100 }));
//       handleUpload?.(fileUrl, fileName);
//     } catch (error) {
//       console.error("Upload error:", error);
//       const message =
//         error.response?.data?.message || "Upload failed. Please try again.";
//       setAlert(
//         <SweetAlert
//           danger
//           title="Upload Failed"
//           onConfirm={() => setAlert(null)}
//           onCancel={() => setAlert(null)}
//         >
//           {message}
//         </SweetAlert>
//       );
//       // Reset progress for failed file
//       setProgress((prev) => {
//         const updated = { ...prev };
//         delete updated[file.name];
//         return updated;
//       });
//     }
//   }

//   // ─── Helpers ──────────────────────────────────────────────────────────────
//   function formatBytes(bytes, decimals = 2) {
//     if (bytes === 0) return "0 Bytes";
//     const k = 1024;
//     const dm = decimals < 0 ? 0 : decimals;
//     const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
//     const i = Math.floor(Math.log(bytes) / Math.log(k));
//     return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
//   }

//   // ─── Render ───────────────────────────────────────────────────────────────
//   return (
//     <React.Fragment>
//       <h6 className="card-title">Upload Files</h6>
//       {alert}
//       <CardSubtitle className="mb-3">
//         Drag and drop files — uploaded directly to the server.
//       </CardSubtitle>

//       <Dropzone
//         accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.pdf,.ai,.eps,.psd,.dst,.pes,.exp,.emb,.cnd,.dsz,.jef,.ofm,.vp3,.hus,.svg"
//         maxSize={5 * 1024 * 1024} // 5 MB
//         onDrop={handleAcceptedFiles}
//         maxFiles={fileLimit}
//         onDropRejected={(fileRejections) => {
//           setAlert(
//             <SweetAlert
//               danger
//               cancelBtnText="Close"
//               title={`You can only upload up to ${fileLimit} file(s).`}
//               onCancel={() => setAlert(null)}
//               onConfirm={() => setAlert(null)}
//               focusCancelBtn
//             >
//               <p>{fileRejections.length} file(s) were rejected.</p>
//             </SweetAlert>
//           );
//         }}
//       >
//         {({ getRootProps, getInputProps }) => (
//           <div className="dropzone" style={{ minHeight: "120px" }}>
//             <div className="dz-message needsclick mt-2" {...getRootProps()} style={{ padding: "10px" }}>
//               <input {...getInputProps()} />
//               <div className="mb-3">
//                 <i className="display-4 text-muted bx bxs-cloud-upload" />
//               </div>
//               <h4>Drop files here or click to upload.</h4>
//               <h6>
//                 Allowed: .jpg .jpeg .png .gif .bmp .tiff .webp .pdf .ai .eps
//                 .psd .dst .pes .exp .emb .cnd .dsz .jef .ofm .vp3 .hus .svg
//               </h6>
//             </div>
//           </div>
//         )}
//       </Dropzone>

//       {/* File Previews with Progress */}
//       <div className="dropzone-previews mt-3" id="file-previews">
//         {selectedFiles.map((f, i) => (
//           <Card
//             className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
//             key={i + "-file"}
//           >
//             <div className="p-2">
//               <Row className="align-items-center">
//                 <Col className="col-auto">
//                   <img
//                     height="80"
//                     className="avatar-sm rounded bg-light"
//                     alt={f.name}
//                     src={f.preview}
//                     onError={(e) => { e.target.src = "/placeholder-file.png"; }}
//                   />
//                 </Col>
//                 <Col>
//                   <Link to="#" className="text-muted font-weight-bold">
//                     {f.name}
//                   </Link>
//                   <p className="mb-0">
//                     <strong>{f.formattedSize}</strong>
//                   </p>

//                   {/* Progress bar */}
//                   {progress[f.name] !== undefined && progress[f.name] < 100 && (
//                     <div style={{ marginTop: "5px" }}>
//                       <progress value={progress[f.name]} max="100" />
//                       <span> {progress[f.name]}%</span>
//                     </div>
//                   )}

//                   {/* Uploaded link */}
//                   {uploadedUrls[f.name] && (
//                     <p className="text-success mt-2">
//                       ✅ Uploaded:{" "}
//                       <a href={uploadedUrls[f.name]} target="_blank" rel="noreferrer">
//                         View File
//                       </a>
//                     </p>
//                   )}
//                 </Col>
//               </Row>
//             </div>
//           </Card>
//         ))}
//       </div>
//     </React.Fragment>
//   );
// };

// FormUpload.propTypes = {
//   onFileChange: PropTypes.func.isRequired,
//   handleUpload: PropTypes.func,
//   orderCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
//   fileLimit: PropTypes.number,
//   onUploadStatusChange: PropTypes.func,
//   orderData: PropTypes.object,
// };

// export default FormUpload;